import React from "react";
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  Spinner,
} from "@fluentui/react-components";
import MaterialReactTable, { MRT_ColumnDef } from "material-react-table";
import { ReportParameter } from "../../../types/reportTypes";
import ASCButton from "../../../components/Button";

interface PopupState {
  data: any[];
  selected: any | null;
  total: number;
  isOpen: boolean;
  pagination: { pageIndex: number; pageSize: number };
  searchQuery: string;
  rowSelection: { [key: string]: boolean };
  isFetchingMore?: boolean;
}

interface PopupTableProps {
  param: ReportParameter;
  popupState: PopupState;
  onClose: () => void;
  onSelect: (selected: any) => void;
  onRowSelectionChange: (updater: any) => void;
  onPaginationChange: (updater: any) => void;
  onGlobalFilterChange: (value: string) => void;
  useScrollPagination?: boolean;
  onLoadMore?: () => void;
}

const PopupTable: React.FC<PopupTableProps> = ({
  param,
  popupState,
  onClose,
  onSelect,
  onRowSelectionChange,
  onPaginationChange,
  onGlobalFilterChange,
  useScrollPagination = false,
  onLoadMore,
}) => {
  const getColumnsForPopup = (param: any): MRT_ColumnDef<any>[] => {
    if (param.apiEndpoint && Array.isArray(param.apiEndpoint.attributes)) {
      return param.apiEndpoint.attributes.map((attr: any) => ({
        accessorKey: attr.value,
        header: attr.label,
        Cell: ({ cell }: any) => {
          const val = cell.getValue();
          if (val == null) return "-";
          if (attr.type === "datetime") {
            const d = new Date(val);
            return isNaN(d.getTime())
              ? val
              : d.toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                });
          }
          if (attr.type === "date") {
            const d = new Date(val);
            return isNaN(d.getTime())
              ? val
              : d.toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                });
          }
          return val;
        },
      }));
    }
    return [];
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!useScrollPagination || !onLoadMore) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 60;
    const hasMore = (popupState.data?.length || 0) < (popupState.total || 0);
    if (nearBottom && !popupState.isFetchingMore && hasMore) {
      onLoadMore();
    }
  };

  return (
    <Dialog
      open={popupState.isOpen}
      onOpenChange={(_, { open }) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogSurface>
        <DialogTitle>{param.placeholder}</DialogTitle>
        <MaterialReactTable
          columns={getColumnsForPopup(param)}
          data={popupState.data || []}
          enableColumnFilters={false}
          enableColumnActions={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableHiding={false}
          positionToolbarAlertBanner="none"
          enableRowSelection
          enablePagination={!useScrollPagination}
          manualPagination={!useScrollPagination}
          enableGlobalFilter={!useScrollPagination}
          enableMultiRowSelection={param.multiSelect}
          rowCount={popupState.total || 0}
          state={{
            pagination: popupState.pagination || {
              pageIndex: 0,
              pageSize: useScrollPagination ? 20 : 10,
            },
            globalFilter: popupState.searchQuery || "",
            rowSelection: popupState.rowSelection || {},
          }}
          onRowSelectionChange={onRowSelectionChange}
          onGlobalFilterChange={(value) => onGlobalFilterChange(value || "")}
          onPaginationChange={onPaginationChange}
          muiTableContainerProps={{
            style: { height: "calc(100vh - 400px)" },
            onScroll: useScrollPagination ? handleScroll : undefined,
          }}
          initialState={{
            density: "compact",
          }}
          muiTablePaginationProps={{
            rowsPerPageOptions: [10, 20],
            showFirstButton: true,
            showLastButton: true,
          }}
          muiSearchTextFieldProps={{
            placeholder: `Search ${param.label}...`,
            sx: { minWidth: "300px" },
          }}
        />
        {useScrollPagination && popupState.isFetchingMore && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "8px",
            }}
          >
            <Spinner size="tiny" label="Loading more..." />
          </div>
        )}
        <div className="dialog_action_button">
          <ASCButton onClick={onClose} label="Cancel" />
          <ASCButton
            className="asc-button-primary"
            appearance="primary"
            onClick={() => {
              const selected = popupState.selected;
              if (
                selected &&
                (Array.isArray(selected) ? selected.length > 0 : true)
              ) {
                onSelect(selected);
                onClose();
              }
            }}
            disabled={
              !popupState.selected ||
              (Array.isArray(popupState.selected) &&
                popupState.selected.length === 0)
            }
            label="Select"
          />
        </div>
      </DialogSurface>
    </Dialog>
  );
};

export default PopupTable;
